import { ReactNode, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { PhoneFrameProvider } from "@/contexts/PhoneFrameContext";

interface PhoneFrameProps {
  children: ReactNode;
}

const BACKGROUNDS = [
  '/backgrounds/bg-1.jpg',
  '/backgrounds/bg-2.jpg',
  '/backgrounds/bg-3.jpg',
  '/backgrounds/bg-4.jpg',
  '/backgrounds/bg-5.jpg',
  '/backgrounds/bg-6.jpg',
];

/**
 * Wraps the app in a phone-sized container on desktop/tablet viewports.
 * On mobile (<768px), renders children full-screen with no wrapper.
 * On desktop: renders inside a phone-proportioned frame centered on an art background.
 * 
 * Marketing site (/) and admin (/admin) bypass the phone frame.
 */
export function PhoneFrame({ children }: PhoneFrameProps) {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const isAppRoute = pathname.startsWith("/app");
  const isAdmin = pathname.startsWith("/admin");
  const [bgIndex] = useState(() => {
    // Rotate background every 24 hours based on the date
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const stored = localStorage.getItem('bg-date');
    const storedIndex = localStorage.getItem('bg-index');
    if (stored === today && storedIndex !== null) {
      return parseInt(storedIndex, 10) % BACKGROUNDS.length;
    }
    const lastIndex = storedIndex !== null ? parseInt(storedIndex, 10) : -1;
    const nextIndex = (lastIndex + 1) % BACKGROUNDS.length;
    localStorage.setItem('bg-date', today);
    localStorage.setItem('bg-index', String(nextIndex));
    return nextIndex;
  });
  const frameRef = useRef<HTMLDivElement>(null);

  // Only show phone frame for /app routes on desktop; marketing + admin render full-screen
  if (isMobile || isMobile === undefined || !isAppRoute || isAdmin) {
    return <>{children}</>;
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-6"
      style={{
        backgroundImage: `url(${BACKGROUNDS[bgIndex]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay to keep phone frame as focal point */}
      <div className="absolute inset-0 bg-black/50" />

      <div
        ref={frameRef}
        className="phone-frame-root w-[390px] max-w-full h-[844px] max-h-full bg-background rounded-[2.5rem] border border-border/30 shadow-2xl relative overflow-hidden z-10"
        style={{ transform: 'translateZ(0)' }}
      >
        <PhoneFrameProvider value={frameRef}>
          {children}
        </PhoneFrameProvider>
      </div>
    </div>
  );
}
